import { ChevronDown } from 'lucide-react';
import type { AbiParameter } from 'viem';
import type { NetworkType } from '@mls-wallet/shared-types';
import { getPolicyApprovalDisplay } from '@/utils/policyFormatting';
import { ContractTxData } from '@/components/transaction-display/ContractTxData';
import { decodeParametersForDisplay } from '@/components/transaction-display/decoders';
import { TransactionBuilderPolicyIndicator } from './TransactionBuilderPolicyIndicator';
import { ImportedTransactionPreview } from './ImportedTransactionPreview';
import { BatchTransactionSummary } from './BatchTransactionSummary';
import {
  getBatchTransactionTypeLabel,
  type BatchTransactionChild,
} from './batch-transaction-draft';

/** Each expandable review row keeps its own policy, parameters and exact bytes. */
export function BatchTransactionReview({
  children,
  network,
}: {
  children: BatchTransactionChild[];
  network: NetworkType;
}) {
  return (
    <section
      aria-label="Batch transactions"
      className="overflow-hidden rounded-[14px] border border-white bg-muted dark:border-zinc-800"
    >
      <h2 className="px-5 py-[7px] text-xs font-normal text-muted-foreground">
        Transactions ({children.length})
      </h2>
      <div className="divide-y divide-border overflow-hidden rounded-[14px] bg-background">
        {children.map((child, index) => {
          const details =
            child.editor?.transactionType === 'contract-interaction'
              ? child.editor.contractInteractionDetails
              : undefined;
          return (
            <details key={child.id} className="group">
              <summary
                aria-label={`Transaction ${index + 1}: ${getBatchTransactionTypeLabel(child)}`}
                className="flex cursor-pointer list-none items-center gap-5 px-[25px] py-6 [&::-webkit-details-marker]:hidden"
              >
                <div className="min-w-0 flex-1 space-y-[5px] text-left">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span>{getBatchTransactionTypeLabel(child)}</span>
                    <span>
                      {child.policy.name} ·{' '}
                      {getPolicyApprovalDisplay(child.policy)}
                    </span>
                  </div>
                  <div className="text-base">
                    <BatchTransactionSummary
                      child={child}
                      network={network}
                      review
                    />
                  </div>
                </div>
                <ChevronDown
                  className="size-6 shrink-0 transition-transform group-open:rotate-180"
                  aria-hidden="true"
                />
              </summary>
              <div className="space-y-5 border-t px-[25px] py-6">
                <TransactionBuilderPolicyIndicator policy={child.policy} />
                {details?.selectedFunction && (
                  <ContractTxData
                    functionName={details.selectedFunction.name}
                    contractAddress={details.address}
                    parameters={decodeParametersForDisplay(
                      details.selectedFunction.inputs as AbiParameter[],
                      details.functionParameters ?? []
                    )}
                    {...(details.nativeTokenValue
                      ? { nativeTokenValue: details.nativeTokenValue }
                      : {})}
                    networkId={network}
                  />
                )}
                <ImportedTransactionPreview transaction={child.payload} />
              </div>
            </details>
          );
        })}
      </div>
    </section>
  );
}
